import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Pagination,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Timeline as TimelineIcon,
  Person as PersonIcon,
  FileCopy as FileIcon,
  Assignment as TaskIcon,
  Email as EmailIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { enhancedApi } from '../api';
import PageLayout from './ui/PageLayout';
import PageHeaderActionsBar from './ui/PageHeaderActionsBar';

const ActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Filtering and pagination state
  const [filters, setFilters] = useState({
    activity_type: '',
    action: '',
    client_id: '',
    date_from: '',
    date_to: '',
    page: 1,
    page_size: 20
  });
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    has_next: false,
    has_previous: false
  });

  // Fetch activities
  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
      
      const response = await enhancedApi.getActivityLog();
      const data = response.data;
      
      if (data.success) {
        setActivities(data.data);
        setPagination(data.pagination);
      } else {
        setError(data.error || 'Failed to fetch activities');
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to fetch activities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await enhancedApi.getActivityLogStats();
      const data = response.data;
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchActivities();
    fetchStats();
  }, [filters.page, filters.page_size]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (event, page) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const handleRefresh = () => {
    fetchActivities();
    fetchStats();
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'File':
        return <FileIcon />;
      case 'Task':
        return <TaskIcon />;
      case 'Client Request':
      case 'Client':
        return <PersonIcon />;
      case 'eSign Request':
        return <EmailIcon />;
      case 'Folder':
        return <FolderIcon />;
      case 'User':
      case 'Permission Set':
      case 'Settings':
      case 'Workflow':
      case 'Note':
      case 'Document Request':
      case 'Document Delivery':
      case 'Invoice':
      case 'Invoice Reminder':
        return <TimelineIcon />;
      default:
        return <TimelineIcon />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'Uploaded':
      case 'Created':
        return 'success';
      case 'Deleted':
        return 'error';
      case 'Moved':
      case 'Updated':
      case 'Edited':
        return 'info';
      case 'Renamed':
        return 'warning';
      case 'sent':
      case 'sent_directly':
      case 'Assigned':
      case 'Completed':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const renderActivitiesTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Activity</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {activities.map((activity) => (
            <TableRow key={activity.id}>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {getActivityIcon(activity.activity_type)}
                  <Typography variant="body2">
                    {activity.item}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip 
                  label={activity.activity_type} 
                  size="small" 
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip 
                  label={activity.action} 
                  size="small" 
                  color={getActionColor(activity.action)}
                />
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ width: 24, height: 24 }}>
                    {activity.action_taken_by?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2">
                    {activity.action_taken_by}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {activity.client || 'N/A'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(activity.timestamp)}
                </Typography>
              </TableCell>
              <TableCell>
                {activity.file_url && (
                  <Tooltip title="View File">
                    <IconButton 
                      size="small" 
                      onClick={() => window.open(activity.file_url, '_blank')}
                    >
                      <FileIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderStatistics = () => {
    if (!stats) return null;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activity Types
              </Typography>
              <List>
                {Object.entries(stats.activity_types).map(([type, count]) => (
                  <ListItem key={type}>
                    <ListItemAvatar>
                      <Avatar>
                        {getActivityIcon(type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={type} 
                      secondary={`${count} activities`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Users
              </Typography>
              <List>
                {stats.top_users.map(([username, count]) => (
                  <ListItem key={username}>
                    <ListItemAvatar>
                      <Avatar>
                        {username.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={username} 
                      secondary={`${count} activities`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Activity (Last 7 Days)
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                {Object.entries(stats.daily_activity).map(([date, count]) => (
                  <Chip 
                    key={date}
                    label={`${date}: ${count}`}
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={filters.activity_type}
                onChange={(e) => handleFilterChange('activity_type', e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="File">File</MenuItem>
                <MenuItem value="Task">Task</MenuItem>
                <MenuItem value="Client Request">Client Request</MenuItem>
                <MenuItem value="eSign Request">eSign Request</MenuItem>
                <MenuItem value="Folder">Folder</MenuItem>
                <MenuItem value="Invoice Reminder">Invoice Reminder</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <MenuItem value="">All Actions</MenuItem>
                <MenuItem value="Uploaded">Uploaded</MenuItem>
                <MenuItem value="Deleted">Deleted</MenuItem>
                <MenuItem value="Moved">Moved</MenuItem>
                <MenuItem value="Renamed">Renamed</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="Marked as completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Date From"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Date To"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (loading && activities.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleOptionSelect = (option) => {
    console.log('ActivityLog received option:', option);
    // Handle different options from sidebar if needed
  };

  return (
    <PageLayout onOptionSelect={handleOptionSelect}>
      <PageHeaderActionsBar
        title="Activity Log"
        secondaryActions={[
          { label: 'Refresh', onClick: handleRefresh, icon: <RefreshIcon />, variant: 'outlined' },
          { label: 'Export', disabled: true, icon: <DownloadIcon />, variant: 'outlined' },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Activities" />
        <Tab label="Statistics" />
      </Tabs>

      {activeTab === 0 && (
        <>
          {renderFilters()}
          
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Recent Activities ({pagination.total_count} total)
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Page Size</InputLabel>
                  <Select
                    value={filters.page_size}
                    onChange={(e) => handleFilterChange('page_size', e.target.value)}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              {renderActivitiesTable()}
              
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.current_page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 1 && renderStatistics()}
    </PageLayout>
  );
};

export default ActivityLog;